import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type {
  Tab,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type { GlobalAssistantAccessDecision } from './global-ai-assistant-access';
import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantContextPolicy,
  GlobalAssistantEntityType,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import { useGlobalAiCommandStore } from './global-ai-assistant-command-store';
import {
  getSingleChartStandardPageMatch,
  getStandardPageMatchForOptionId,
} from './global-ai-assistant-standard-page-match';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import type { HeadlessGeneratedPage } from './global-ai-generate-headless';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import { hasOfferMarker, type OfferKind } from './offer-marker';
import { stashGeneratedPage } from './pending-generated-page';
import type { ReasoningTrace } from './reasoning-trace';
import { buildFilterResolutionDigest } from './turn-outcome-digest';

// Which chip kind each offer marker suppresses AND the transcript message
// `kind` that must back it for the inline trigger to render. Suppression is
// gated on this: a chip is hidden only when the stamped id resolves to a live
// assistant message of the matching kind (the trigger will actually render).
const KIND_BY_OFFER = {
  nav: 'nav_confirm',
  build: 'create_page_confirm',
  page: 'single_chart',
  report: 'report_offers',
  custom: 'single_chart',
  filters: 'single_chart',
} as const satisfies Record<OfferKind, string>;

export function useGlobalAiOfferWiring(args: {
  transcript: GlobalAiTranscriptMessage[];
  isTurnRunning: boolean;
  isCommandInFlight: boolean;
  userTabs: Tab[];
  customDashAccess: GlobalAssistantAccessDecision;
  navigateToMatch: (
    match: GlobalAssistantSuggestMatch,
    options?: {
      standard?: boolean;
      prompt?: string;
      skipFilterApply?: boolean;
      resolvedFilters?: TalentDiscoveryAiFilterSearchResponseV2;
      resolvedEntityType?: GlobalAssistantEntityType;
    }
  ) => Promise<{ navigated: boolean }>;
  startCustomPageCreate: (
    entityType: CustomDashAssistantEntityType,
    options?: {
      prompt?: string;
      contextPolicy?: GlobalAssistantContextPolicy;
      skipBuild?: boolean;
    }
  ) => { ok: boolean; targetPath?: string };
  /**
   * Lazy click-time headless page generation for build chips that carry NO
   * pre-generated layout. `page` resolves null on failure/questions → legacy
   * flow. On success, `buildTrace` is the click's ended build trace: the
   * landing narration reopens it so "Reading the page data" joins the same
   * working block instead of opening a second one.
   */
  generateForBuildClick: (
    prompt: string,
    entityType: CustomDashAssistantEntityType
  ) => Promise<{
    page: HeadlessGeneratedPage | null;
    buildTrace?: { traceId: string; trace: ReasoningTrace };
  }>;
  /**
   * Build the consume-once `onSeeded` callback a resolve-first build stashes
   * with its generated page: it arms the landing-narration collection
   * ("Reading the page data" step → digest-seeded silent narrate turn) once
   * `CustomDashRoute` reports the minted widget ids. See
   * seeded-build-narration.ts.
   */
  beginSeededBuildNarration: (params: {
    prompt: string;
    targetPathname: string;
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  }) => (seededItems: PositionedItem[]) => void;
  markNavConfirmResolved: (id: string) => void;
  markCreatePageConfirmResolved: (id: string) => void;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
  armNavResume: (next: {
    continuationPrompt: string;
    pageName: string;
    expectedPath: string;
  }) => void;
  armCreatePageResume: (next: { seed: string; expectedPath: string }) => void;
  armBuildHere: () => void;
  transcriptScrollRoot: HTMLDivElement | null;
}) {
  const location = useLocation();

  // Offer lookup index, built per transcript change in TWO passes.
  // Narration triggers resolve their action by the `offerTargets` id STAMPED
  // on the text message by the client tool that appended it — never by
  // transcript position — so no adjacency/turn heuristic exists to go stale.
  // `stampedOfferKindsByTargetId` inverts the stamps PER-KIND (targetId -> set
  // of offer kinds): a chip is suppressed ONLY when a narration stamped ITS id,
  // carried that kind's marker, AND the stamped id RESOLVES to a live assistant
  // message of the matching kind — i.e. only when the inline trigger will
  // actually render. Suppression thus equals renderability: a dangling or
  // mistyped stamp (id absent, or resolving to the wrong kind) can no longer
  // hide a chip whose replacement trigger degrades to plain text, so the user
  // never gets neither a link nor a button. Building `byId` first (pass 1) lets
  // pass 2 do this resolution. Per-kind (not a flat id set) also lets one chart
  // id be stamped for some kinds but not others.
  const { messagesById, stampedOfferKindsByTargetId } = useMemo(() => {
    // Pass 1: id -> message, so pass 2 can resolve each stamp's target.
    const byId = new Map<string, GlobalAiTranscriptMessage>();
    for (const m of args.transcript) {
      byId.set(m.id, m);
    }
    // Pass 2: validate each stamp against `byId` and record the suppression
    // only when the target is renderable (resolves to the matching kind).
    const stampedKinds = new Map<string, Set<OfferKind>>();
    for (const m of args.transcript) {
      if (
        m.role !== 'assistant' ||
        !('kind' in m) ||
        m.kind !== 'text' ||
        !m.offerTargets
      ) {
        continue;
      }
      for (const kind of Object.keys(KIND_BY_OFFER) as OfferKind[]) {
        const targetId = m.offerTargets[kind];
        if (!targetId || !hasOfferMarker(m.text, kind)) {
          continue;
        }
        // Suppress the chip ONLY when the inline trigger can actually render:
        // the stamped id must resolve to a live assistant message of the
        // matching kind. A dangling/mistyped stamp keeps the chip as the
        // fallback affordance.
        const target = byId.get(targetId);
        const renderable =
          target &&
          target.role === 'assistant' &&
          'kind' in target &&
          target.kind === KIND_BY_OFFER[kind];
        if (!renderable) {
          continue;
        }
        const kinds = stampedKinds.get(targetId) ?? new Set<OfferKind>();
        kinds.add(kind);
        stampedKinds.set(targetId, kinds);
      }
    }
    return {
      messagesById: byId,
      stampedOfferKindsByTargetId: stampedKinds,
    };
  }, [args.transcript]);

  // Fire the nav offer: the guarded navigate + resolve + arm/fire resume that
  // the `nav_confirm` chip performs. Shared by the chip and the inline narration
  // trigger so both fire the exact same action (no divergence). Only ever runs
  // on an explicit user click — nothing auto-navigates, on home or off-home.
  const fireNavOffer = async (
    navMsg: Extract<GlobalAiTranscriptMessage, { kind: 'nav_confirm' }>
  ) => {
    // Mid-stream guard, mirrors the chips' `isDisabled`: never fire while a
    // turn/command is in flight.
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    const navMatch = getStandardPageMatchForOptionId(navMsg.optionId, {});
    const access = navMatch
      ? getGlobalAssistantRouteAccess(navMatch.navigatePath, args.userTabs)
      : null;
    if (!navMatch || !access?.allowed) {
      return;
    }
    if (navMsg.resolved) {
      return;
    }
    const alreadyOnTarget = globalAssistantPathsMatch(
      location.pathname,
      navMatch.navigatePath
    );
    // The guarded navigation: the unsaved-changes redirect guard inside
    // navigateToMatch can CANCEL it. Only proceed (resolve + arm/fire the
    // resume) when navigation actually happened, so a cancelled click leaves
    // the offer live to retry.
    // DATA-GROUNDED nav (offer_page_or_custom with a filterArtifactId): the
    // message carries the answer's exact resolved artifact — apply IT on the
    // destination so the page shows the same slice the narrated figures came
    // from (text=link; landing on the page's default primary instead was the
    // live 2026-07-08 miss). Otherwise skipFilterApply: the `navigate` tool is
    // pure navigation — without it, navigateToMatch falls back to
    // lastUserPrompt and fires a stale discovery resolve+apply. Any
    // chained filtering belongs to the resumed turn (continuationPrompt),
    // which can call apply_filters properly.
    const { navigated } = await args.navigateToMatch(
      navMatch,
      navMsg.filters
        ? {
            standard: true,
            resolvedFilters: navMsg.filters,
            resolvedEntityType: navMsg.entityType,
          }
        : {
            standard: true,
            skipFilterApply: true,
          }
    );
    if (!navigated) {
      return;
    }
    args.markNavConfirmResolved(navMsg.id);
    if (!navMsg.continuationPrompt) {
      return;
    }
    if (alreadyOnTarget) {
      // No navigation occurred (already on the destination), so the
      // path-keyed effect won't see a change — fire the resume immediately.
      // Context is already correct.
      void args.startAssistantTurn(
        `[System: The user clicked the confirmation button and you have ALREADY navigated to ${navMsg.pageName}. The navigation is done — do NOT navigate again or offer another navigation button. If the request was only to open this page, briefly confirm you are here and end your turn; otherwise continue any remaining task on this page.] ${navMsg.continuationPrompt}`,
        { silent: true }
      );
      return;
    }
    // ARM the resume; the location-keyed effect fires it once the router lands
    // on the destination, so the resumed turn reads the DESTINATION page
    // context (not the page we left).
    args.armNavResume({
      continuationPrompt: navMsg.continuationPrompt,
      pageName: navMsg.pageName,
      expectedPath: navMatch.navigatePath,
    });
  };

  // Single source for a single-value answer's standard-page offer state: the
  // derived page match and its route access. Shared by the inline trigger's
  // fire handler, the "Go to <page>" chip, and the text-branch trigger-liveness
  // check, so the three can never diverge on what counts as an offerable page.
  const getStandardPageOfferState = (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    const match = chartMsg.standardPagePath
      ? getSingleChartStandardPageMatch(chartMsg)
      : null;
    if (!match) {
      return null;
    }
    return {
      match,
      access: getGlobalAssistantRouteAccess(match.navigatePath, args.userTabs),
    };
  };

  // Fire the single-value page offer: navigate to the standard page backing a
  // single-value answer AND apply the question's filters — the exact action the
  // "Go to <page>" chip performs, shared by that chip and the inline narration
  // trigger. NOT auto-navigation: only ever runs on an explicit click.
  const fireStandardPageOffer = async (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    const offer = getStandardPageOfferState(chartMsg);
    if (!offer?.access.allowed) {
      return;
    }
    // Deterministic click: the single_chart message carries the EXACT resolved
    // artifact the answer was computed from (`filters`, appended by
    // `renderChartAndAwaitDigest`). Pass it through so the destination applies
    // that artifact directly — never an independent re-resolution of the
    // prompt, which can land on a completely different filter set than the
    // answer (e.g. taxonomy-31 "Nurse" 208k vs "Professionals" 3.7M). The
    // prompt-only fallback remains ONLY for legacy persisted messages that
    // predate the artifact.
    void args.navigateToMatch(offer.match, {
      standard: true,
      prompt: chartMsg.prompt,
      ...(chartMsg.filters
        ? {
            resolvedFilters: chartMsg.filters,
            resolvedEntityType: chartMsg.entityType,
          }
        : {}),
    });
  };

  // Fire the build offer: the create-draft + navigate + arm/fire build resume
  // that the `create_page_confirm` chip performs. Shared by the chip and the
  // inline narration trigger. Build offers are NOT navigation — never auto-fired
  // on home; they stay explicit clicks.
  const fireBuildOffer = async (
    buildMsg: Extract<
      GlobalAiTranscriptMessage,
      { kind: 'create_page_confirm' }
    >
  ) => {
    if (args.isTurnRunning || args.isCommandInFlight) {
      return;
    }
    if (!args.customDashAccess.allowed || buildMsg.resolved) {
      return;
    }
    // RESOLVE-FIRST (Phase C): when the chip carries a PRE-GENERATED layout,
    // the click seeds a fresh draft with it and navigates — landing shows the
    // complete page, widgets data-load, and NO turn resumes (the generation
    // already happened during the offering turn). Filters ride the command
    // store's emit-before-navigate contract, claimed by the destination
    // bridge on mount. No generated payload → legacy confirm-navigate-resume.
    // Layout source: pre-generated during the offering turn
    // (create_custom_page), or generated LAZILY now (offer_page_or_custom's
    // secondary chip) — either way the navigation happens AFTER generation
    // (resolve-first), landing on a complete draft. Lazy failure → legacy.
    const lazy = buildMsg.generated
      ? undefined
      : await args.generateForBuildClick(buildMsg.prompt, buildMsg.entityType);
    const generatedPage = buildMsg.generated ?? lazy?.page ?? null;
    // The lazy click's build trace — the landing narration's reading step
    // resumes it (one click, one working block). The pre-generated chip has
    // no click-time trace, so its reading step opens its own small block.
    const buildTrace = lazy?.buildTrace;
    if (generatedPage) {
      const { ok, targetPath } = args.startCustomPageCreate(
        buildMsg.entityType,
        { prompt: buildMsg.prompt, skipBuild: true }
      );
      if (!ok || !targetPath) {
        return;
      }
      // The draft path carries the current SEARCH string ("/company?filters=…")
      // but both consumers match on the bare pathname — the stash store
      // normalizes internally, and the bridge's applyRouteFilters claim
      // compares against location.pathname, so the command MUST be emitted
      // with the pathname or it is never claimed (verified live: layout
      // landed, Google filter silently didn't).
      const targetPathname = targetPath.split('?')[0] ?? targetPath;
      // Landing filters: the page-gen's own resolution when it actually
      // resolved something, else the offer's DATA-GROUNDED artifact
      // (buildMsg.filters, offer_page_or_custom's filterArtifactId). The
      // page-gen filter search can come back "successfully empty" (expr with
      // no children + the named entity in unknownFilters — observed live:
      // "Ford" unmatched), and applying that empty set lands the draft on the
      // page's DEFAULT primary; the grounded artifact then still carries the
      // slice the offer promised.
      const generatedResolvedSomething =
        generatedPage.filters !== undefined &&
        buildFilterResolutionDigest({
          response: generatedPage.filters,
          targetPage: targetPathname,
        }).applied.length > 0;
      const landingFilters = generatedResolvedSomething
        ? generatedPage.filters
        : (buildMsg.filters ?? generatedPage.filters);
      // Stash BEFORE React processes the route change (same tick): the
      // mounting NewCustomPage consumes it and seeds the draft config. The
      // onSeeded callback restores the GROUNDED landing narration the legacy
      // resume flow had: once the consumer reports the minted widget ids, the
      // drawer collects their settled digests ("Reading the page data") and
      // narrates the finished page from them.
      stashGeneratedPage(targetPathname, {
        content: generatedPage.content,
        ...(landingFilters ? { filters: landingFilters } : {}),
        prompt: buildMsg.prompt,
        onSeeded: args.beginSeededBuildNarration({
          prompt: buildMsg.prompt,
          targetPathname,
          ...(landingFilters ? { filters: landingFilters } : {}),
          ...(buildTrace ? { resumeTrace: buildTrace } : {}),
        }),
      });
      if (landingFilters) {
        useGlobalAiCommandStore.getState().emit({
          kind: 'applyRouteFilters',
          requestId: crypto.randomUUID(),
          filterKind: 'tdV2',
          response: landingFilters,
          targetPath: targetPathname,
          entityType: buildMsg.entityType,
          prompt: buildMsg.prompt,
        });
      }
      // Do NOT mark this chip resolved: the finished layout persists on the
      // message (or regenerates for the lazy path), so the build stays a
      // repeatable action. A draft is unsaved — after the user navigates away it
      // is gone, and clicking the chip again must rebuild + navigate (a fresh
      // draft), not dead-no-op. Only the legacy resume path below marks resolved
      // (its click arms a one-shot turn resume that must not double-fire).
      return;
    }
    // Confirm-navigate-resume: the click IS the confirmation. (1) Create an
    // empty draft + navigate to it WITHOUT emitting the build. (2) ARM the
    // build-here ref so the resumed turn's `create_custom_page` call BUILDS on
    // this draft (CREATE semantics) instead of offering again. (3) Resume the
    // loop turn (silently) seeded with the full original prompt + a "you're on
    // a new empty page, build it here by calling create_custom_page" system
    // note, so the model runs the CREATE page-gen on the draft. Using CREATE —
    // not update_current_page — keeps the model's reasoning/narration framed as
    // a fresh build, not a modification.
    const { ok, targetPath } = args.startCustomPageCreate(buildMsg.entityType, {
      prompt: buildMsg.prompt,
      skipBuild: true,
    });
    if (!ok || !targetPath) {
      return;
    }
    args.markCreatePageConfirmResolved(buildMsg.id);
    // Arm the build-here signal BEFORE the resume turn runs so the loop's
    // `create_custom_page` handle builds the draft (mode: 'create', no nav)
    // rather than offering again. The runner resets it on consumption.
    args.armBuildHere();
    const seed = `[System: The user clicked "Build the dashboard", which navigated you to a NEW EMPTY editable custom dashboard. Build it out ON THIS PAGE now by calling create_custom_page with their request — it will populate THIS page. create_custom_page's narration input still requires an [phrase](offer:build) marker: pass a short note like "[Building the dashboard](offer:build)" — it is not shown to the user. Do NOT navigate, create another page, call update_current_page, render a single chart, offer another button, or offer a report (a report was already offered with the build button). After the page is built, narrate it and end your turn.] ${buildMsg.prompt}`;
    if (globalAssistantPathsMatch(location.pathname, targetPath)) {
      // The router already reflects the draft path (no further navigation
      // re-render will fire the path-keyed effect), so resume now. Context is
      // already the empty draft. The bracketed seed is a system instruction —
      // fire it silently so it never renders as a user chat bubble.
      void args.startAssistantTurn(seed, {
        silent: true,
        suppressReportOffer: true,
      });
      const transcriptScrollRoot = args.transcriptScrollRoot;
      if (transcriptScrollRoot) {
        requestAnimationFrame(() => {
          transcriptScrollRoot.scrollTop = transcriptScrollRoot.scrollHeight;
        });
      }
      return;
    }
    // ARM the resume; the location-keyed effect fires it once the router lands
    // on the empty draft, so the resumed turn reads the draft's context
    // (in_custom_dash_edit_mode).
    args.armCreatePageResume({
      seed,
      expectedPath: targetPath,
    });
  };

  return {
    messagesById,
    stampedOfferKindsByTargetId,
    fireNavOffer,
    fireBuildOffer,
    fireStandardPageOffer,
    getStandardPageOfferState,
  };
}
